import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send, CalendarDays } from "lucide-react";
import { siteInfo } from "../mock";
import { useToast } from "../hooks/use-toast";
import { useBooking } from "../context/BookingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = ({ compact = false }) => {
  const { toast } = useToast();
  const booking = useBooking();
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({
        title: "Bericht verzonden!",
        description: "Bedankt voor uw bericht. Wij nemen zo snel mogelijk contact op.",
      });
      setForm({ name: "", email: "", phone: "", category: "", message: "" });
    } catch (err) {
      toast({
        title: "Er ging iets mis",
        description: "Probeer het opnieuw of bel ons direct op 06-12562049.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-[1280px] mx-auto px-5 md:px-10">
        {!compact && (
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm tracking-[0.3em] uppercase text-neutral-500 mb-3">
              — Contact
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-5">Contact</h2>
            <p className="text-lg text-neutral-600 leading-relaxed mb-6">
              Neem vandaag nog contact met ons op en ervaar onze service zelf.
            </p>
            <button
              onClick={booking.open}
              className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-black text-white px-6 py-3 text-sm font-medium transition-colors"
            >
              <CalendarDays size={15} />
              Maak direct een afspraak
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Info */}
          <div className="bg-neutral-50 border border-neutral-200 p-8 md:p-10">
            <h3 className="text-2xl font-bold text-neutral-900 mb-8">Ons contact</h3>

            <div className="space-y-7">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-neutral-900 text-white flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Adres</p>
                  <p className="text-neutral-900 font-medium">
                    {siteInfo.address.street}
                    <br />
                    {siteInfo.address.zip}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-neutral-900 text-white flex items-center justify-center flex-shrink-0">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Telefoon</p>
                  <a href={`tel:${siteInfo.phoneRaw}`} className="text-neutral-900 font-medium hover:underline">
                    {siteInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-neutral-900 text-white flex items-center justify-center flex-shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">E-mail</p>
                  <a href={`mailto:${siteInfo.email}`} className="text-neutral-900 font-medium hover:underline">
                    {siteInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-neutral-900 text-white flex items-center justify-center flex-shrink-0">
                  <Clock size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
                    Openingstijden
                  </p>
                  <ul className="space-y-1 text-sm">
                    {siteInfo.hours.map((h) => (
                      <li
                        key={h.day}
                        className="flex justify-between text-neutral-700 border-b border-neutral-200/70 py-1"
                      >
                        <span>{h.day}</span>
                        <span className="text-neutral-900 font-medium">{h.hours}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <h3 className="text-2xl font-bold text-neutral-900 mb-2">Stuur ons een bericht</h3>
            <p className="text-neutral-600 mb-6">Vul het formulier in, wij reageren binnen 24 uur.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-neutral-600 mb-2">
                  Naam
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-neutral-900 transition-colors bg-white"
                  placeholder="Uw naam"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-neutral-600 mb-2">
                  Telefoon
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-neutral-900 transition-colors bg-white"
                  placeholder="06-12345678"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-600 mb-2">
                E-mail
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-neutral-900 transition-colors bg-white"
                placeholder="uw@email.nl"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-600 mb-2">
                Waar kunnen we u mee helpen? <span className="text-rose-600">*</span>
              </label>
              <select
                required
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-neutral-900 transition-colors bg-white text-neutral-900"
                data-testid="contact-category"
              >
                <option value="">— Maak een keuze —</option>
                <option value="Losse reparatie">Losse reparatie</option>
                <option value="IT Support particulier">IT Support particulier</option>
                <option value="IT Support zakelijk">IT Support zakelijk</option>
                <option value="Anders">Anders</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-600 mb-2">
                Bericht
              </label>
              <textarea
                required
                rows="6"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-neutral-900 transition-colors bg-white resize-none"
                placeholder="Beschrijf uw probleem of vraag..."
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center gap-2 bg-neutral-900 hover:bg-black text-white px-7 py-4 text-[15px] font-medium tracking-wide transition-colors disabled:opacity-60"
            >
              {submitting ? "Verzenden..." : "Verzend bericht"}
              <Send size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>

        {/* Map */}
        <div className="mt-16 border border-neutral-200 overflow-hidden">
          <iframe
            title="Computheer locatie"
            src="https://www.google.com/maps?q=Zeestraat+183,+1943AB+Beverwijk&output=embed"
            width="100%"
            height="400"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
